import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiQuery } from '../useApiQuery';
import { useApiMutation } from '../useApiMutation';
import { apiGet, apiPost, apiPut, apiDelete, operationsClient } from '@/lib/api';
import { promotionsEndpoints } from '@/lib/api/endpoints';
import { promotionKeys } from '@/lib/queryKeys';
import type {
  Promotion,
  PromotionListParams,
  PromotionListResponse,
  CreatePromotionDto,
  UpdatePromotionDto,
} from '@/types/promotion';

interface UsePromotionsOptions {
  enabled?: boolean;
}

/**
 * Fetches the list of promotions from the operations microservice.
 * Optionally filter by status (active | upcoming | expired).
 */
export function usePromotions(
  params: PromotionListParams = {},
  options: UsePromotionsOptions = {},
) {
  return useApiQuery<PromotionListResponse>({
    queryKey: promotionKeys.list(params),
    queryFn: () =>
      apiGet<PromotionListResponse>(operationsClient, promotionsEndpoints.listBase(), { params }),
    enabled: options.enabled ?? true,
    showErrorToast: false,
  });
}

/**
 * Fetches a single promotion by ID.
 */
export function usePromotion(id: string) {
  return useApiQuery<Promotion>({
    queryKey: promotionKeys.detail(id),
    queryFn: () => apiGet<Promotion>(operationsClient, promotionsEndpoints.detail(id)),
    enabled: Boolean(id),
  });
}

/**
 * Creates a new promotion.
 */
export function useCreatePromotion() {
  const queryClient = useQueryClient();

  return useApiMutation<Promotion, CreatePromotionDto>({
    mutationFn: (dto) =>
      apiPost<Promotion, CreatePromotionDto>(operationsClient, promotionsEndpoints.create(), dto),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: promotionKeys.lists() });
      toast.success('Promotion created');
    },
  });
}

/**
 * Updates an existing promotion.
 */
export function useUpdatePromotion() {
  const queryClient = useQueryClient();

  return useApiMutation<Promotion, UpdatePromotionDto & { id: string }>({
    mutationFn: ({ id, ...dto }) =>
      apiPut<Promotion, UpdatePromotionDto>(operationsClient, promotionsEndpoints.update(id), dto),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: promotionKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: promotionKeys.detail(data.id) });
      toast.success('Promotion updated');
    },
  });
}

/**
 * Deletes a promotion by ID.
 */
export function useDeletePromotion() {
  const queryClient = useQueryClient();

  return useApiMutation<void, string>({
    mutationFn: (id) => apiDelete<void>(operationsClient, promotionsEndpoints.delete(id)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: promotionKeys.lists() });
      toast.success('Promotion deleted');
    },
  });
}
